import {
    ArrowUp,
    ChevronDown,
    ChevronUp,
    MessageSquareText,
} from 'lucide-react';
import {
    type ChangeEvent,
    type KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { buildActivityAssigneeOptions } from '@apps/web/components/home/activity_assignment';
import { Button } from '@apps/web/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import { useActivityEventAssignees } from '@apps/web/swr_data_fetching/activity_event_assignments';
import { useUser } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type ConversationMessageNoteRead,
    type ConversationMessageNotesResponse,
} from '@libraries/typescript/api_client';

const NOTE_MAX_LENGTH = 4000;

type ConversationMessageNotesProps = {
    readonly organizationId: string;
    readonly conversationId: string;
    readonly messageId: string;
    readonly timezone?: string;
    readonly isInbound: boolean;
    readonly collapsed?: boolean;
    readonly onCollapse: () => void;
    readonly onExpand?: () => void;
    readonly onNoteCreated?: () => void;
    readonly summary?: ConversationMessageNotesSummary | null;
    readonly onSummaryChange?: (
        summary: ConversationMessageNotesSummary,
    ) => void;
    readonly onConversationMutated?: () => Promise<unknown> | void;
};

export type ConversationMessageNotesSummary = {
    readonly commentCount: number;
    readonly hasMore: boolean;
    readonly latestCreatedAt: string;
    readonly latestAuthorInitial: string;
};

function noteTimestamp(createdAt: string, timezone?: string): string {
    return new Date(createdAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...(timezone && { timeZone: timezone }),
    });
}

function mentionQueryAtCursor(
    value: string,
    cursor: number | null,
): string | null {
    if (cursor === null) {
        return null;
    }
    const prefix = value.slice(0, cursor);
    const match = /(?:^|\s)@([^@\n]*)$/.exec(prefix);
    return match?.[1] ?? null;
}

function mergeNotes(
    olderNotes: ReadonlyArray<ConversationMessageNoteRead>,
    currentNotes: ReadonlyArray<ConversationMessageNoteRead>,
): Array<ConversationMessageNoteRead> {
    const seenIds = new Set(currentNotes.map((note) => note.id));
    return [
        ...olderNotes.filter((note) => !seenIds.has(note.id)),
        ...currentNotes,
    ];
}

export function ConversationMessageNotes({
    isInbound,
    collapsed = false,
    summary,
    ...threadProps
}: ConversationMessageNotesProps) {
    return (
        <div
            className={cn(
                'mt-1 flex',
                isInbound ? 'justify-start pl-10' : 'justify-end pr-10',
            )}
        >
            <div
                className="w-full max-w-[min(80%,420px)]"
                data-testid="internal-note-thread-container"
            >
                {collapsed ? (
                    <CollapsedConversationMessageNotes
                        onExpand={threadProps.onExpand}
                        summary={summary}
                        timezone={threadProps.timezone}
                    />
                ) : (
                    <ConversationMessageNotesThread {...threadProps} />
                )}
            </div>
        </div>
    );
}

function CollapsedConversationMessageNotes({
    onExpand,
    summary,
    timezone,
}: Pick<ConversationMessageNotesProps, 'onExpand' | 'summary' | 'timezone'>) {
    const commentCountLabel = summary
        ? `${summary.commentCount}${summary.hasMore ? '+' : ''} ${summary.commentCount === 1 && !summary.hasMore ? 'comment' : 'comments'}`
        : 'Internal thread';

    return (
        <button
            aria-label="Expand internal thread"
            className="bg-background hover:bg-muted/50 flex min-h-12 w-full items-center gap-2 rounded-xl border px-3 py-2 text-left shadow-sm transition-colors"
            onClick={onExpand}
            type="button"
        >
            {summary ? (
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-200 text-xs font-medium text-amber-950 dark:bg-amber-800 dark:text-amber-50">
                    {summary.latestAuthorInitial}
                </span>
            ) : (
                <MessageSquareText className="text-muted-foreground size-4 shrink-0" />
            )}
            <span className="text-sm font-medium">{commentCountLabel}</span>
            {summary ? (
                <span className="text-muted-foreground min-w-0 truncate text-sm">
                    {noteTimestamp(summary.latestCreatedAt, timezone)}
                </span>
            ) : null}
            <ChevronDown className="text-muted-foreground ml-auto size-4 shrink-0" />
        </button>
    );
}

function ConversationMessageNotesThread({
    organizationId,
    conversationId,
    messageId,
    timezone,
    onCollapse,
    onNoteCreated,
    onSummaryChange,
    onConversationMutated,
}: Omit<
    ConversationMessageNotesProps,
    'collapsed' | 'isInbound' | 'onExpand' | 'summary'
>) {
    const [notes, setNotes] = useState<Array<ConversationMessageNoteRead>>([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextBeforeCreatedAt, setNextBeforeCreatedAt] = useState<
        string | null
    >(null);
    const [nextBeforeId, setNextBeforeId] = useState<string | null>(null);
    const [loadState, setLoadState] = useState<
        | { readonly status: 'ready'; readonly hasLoaded: true }
        | {
              readonly status: 'loading' | 'error' | 'retrying';
              readonly hasLoaded: boolean;
          }
    >({ status: 'loading', hasLoaded: false });
    const loading =
        loadState.status === 'loading' || loadState.status === 'retrying';
    const loadError =
        loadState.status === 'error' || loadState.status === 'retrying';
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [draft, setDraft] = useState('');
    const [mentionQuery, setMentionQuery] = useState<string | null>(null);
    const [mentionedUserId, setMentionedUserId] = useState<string | null>(null);
    const [mentionedUserName, setMentionedUserName] = useState<string | null>(
        null,
    );
    const [activeMentionUserId, setActiveMentionUserId] = useState<
        string | null
    >(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const hasLoadedOlderRef = useRef(false);
    const latestLoadRef = useRef(0);
    const { data: currentUser } = useUser({
        reason: 'conversation-message-notes',
    });
    const { data: assigneesData } = useActivityEventAssignees(organizationId, [
        organizationId,
    ]);
    const assigneeOptions = useMemo(
        () => buildActivityAssigneeOptions(assigneesData?.users ?? []),
        [assigneesData?.users],
    );
    const filteredAssigneeOptions = useMemo(() => {
        const normalizedQuery = mentionQuery?.trim().toLocaleLowerCase() ?? '';
        return assigneeOptions.filter((option) =>
            option.label.toLocaleLowerCase().includes(normalizedQuery),
        );
    }, [assigneeOptions, mentionQuery]);

    useEffect(() => {
        if (mentionQuery === null) {
            setActiveMentionUserId(null);
            return;
        }
        setActiveMentionUserId((currentUserId) =>
            filteredAssigneeOptions.some(
                (option) => option.id === currentUserId,
            )
                ? currentUserId
                : (filteredAssigneeOptions[0]?.id ?? null),
        );
    }, [filteredAssigneeOptions, mentionQuery]);

    const loadLatest = useCallback(async () => {
        const loadId = ++latestLoadRef.current;
        setLoadState((current) => ({
            ...current,
            status:
                current.status === 'error' || current.status === 'retrying'
                    ? 'retrying'
                    : 'loading',
        }));
        try {
            const response = await getApiClient().get({
                url: '/organizations/{organization_id}/conversations/{conversation_id}/messages/{message_id}/notes',
                pathParams: {
                    organization_id: organizationId,
                    conversation_id: conversationId,
                    message_id: messageId,
                },
                reason: 'conversation-message-notes',
            });
            if (loadId !== latestLoadRef.current) {
                return;
            }
            setNotes((currentNotes) =>
                mergeNotes(currentNotes, response.notes),
            );
            setLoadState({ status: 'ready', hasLoaded: true });
            if (!hasLoadedOlderRef.current) {
                setHasMore(response.has_more);
                setNextBeforeCreatedAt(response.next_before_created_at ?? null);
                setNextBeforeId(response.next_before_id ?? null);
            }
        } catch (error) {
            if (loadId !== latestLoadRef.current) {
                return;
            }
            console.error('Failed to load internal notes', {
                organizationId,
                conversationId,
                messageId,
                errorName:
                    error instanceof Error ? error.name : 'Unknown error',
                status: error instanceof ApiError ? error.status : undefined,
                requestId:
                    error instanceof ApiError ? error.requestId : undefined,
            });
            setLoadState((current) => ({ ...current, status: 'error' }));
        }
    }, [conversationId, messageId, organizationId]);

    useEffect(() => {
        void loadLatest();
        return () => {
            latestLoadRef.current += 1;
        };
    }, [loadLatest]);

    useEffect(() => {
        if (notes.length === 0) {
            return;
        }
        const latestNote = notes[notes.length - 1];
        if (!latestNote) {
            return;
        }
        onSummaryChange?.({
            commentCount: notes.length,
            hasMore,
            latestCreatedAt: latestNote.created_at,
            latestAuthorInitial:
                latestNote.created_by_user_name
                    .trim()
                    .charAt(0)
                    .toUpperCase() || '?',
        });
    }, [hasMore, notes, onSummaryChange]);

    useListenForOrganizationEvent(
        'conversation_updated',
        (event) => {
            if (event.resource.id === conversationId) {
                void loadLatest();
            }
        },
        { organizationId, throttleMs: 500 },
    );

    async function loadOlderNotes() {
        if (loadingOlder || !hasMore || !nextBeforeCreatedAt || !nextBeforeId) {
            return;
        }
        setLoadingOlder(true);
        try {
            const response: ConversationMessageNotesResponse =
                await getApiClient().get({
                    url: '/organizations/{organization_id}/conversations/{conversation_id}/messages/{message_id}/notes',
                    pathParams: {
                        organization_id: organizationId,
                        conversation_id: conversationId,
                        message_id: messageId,
                    },
                    queryParams: {
                        before_created_at: nextBeforeCreatedAt,
                        before_id: nextBeforeId,
                    },
                    reason: 'conversation-message-notes-older',
                });
            hasLoadedOlderRef.current = true;
            setNotes((currentNotes) =>
                mergeNotes(response.notes, currentNotes),
            );
            setHasMore(response.has_more);
            setNextBeforeCreatedAt(response.next_before_created_at ?? null);
            setNextBeforeId(response.next_before_id ?? null);
        } catch {
            toast.error('Couldn’t load earlier notes', {
                persistNotification: false,
            });
        } finally {
            setLoadingOlder(false);
        }
    }

    function handleDraftChange(event: ChangeEvent<HTMLTextAreaElement>) {
        const nextDraft = event.target.value;
        setDraft(nextDraft);
        if (mentionedUserId !== null) {
            if (
                mentionedUserName !== null &&
                !nextDraft.includes(`@${mentionedUserName}`)
            ) {
                setMentionedUserId(null);
                setMentionedUserName(null);
                setMentionQuery(
                    mentionQueryAtCursor(
                        nextDraft,
                        event.target.selectionStart,
                    ),
                );
                return;
            }
            setMentionQuery(null);
            return;
        }
        setMentionQuery(
            mentionQueryAtCursor(nextDraft, event.target.selectionStart),
        );
    }

    function selectMention(userId: string, displayName: string) {
        const textarea = textareaRef.current;
        const cursor = textarea?.selectionStart ?? draft.length;
        const prefix = draft.slice(0, cursor);
        const mentionStart = prefix.lastIndexOf('@');
        if (mentionStart < 0) {
            setMentionQuery(null);
            return;
        }
        const nextDraft = `${draft.slice(0, mentionStart)}@${displayName} ${draft.slice(cursor)}`;
        setDraft(nextDraft);
        setMentionedUserId(userId);
        setMentionedUserName(displayName);
        setMentionQuery(null);
        requestAnimationFrame(() => {
            const nextCursor = mentionStart + displayName.length + 2;
            textarea?.focus();
            textarea?.setSelectionRange(nextCursor, nextCursor);
        });
    }

    function handleDraftKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
        if (mentionQuery === null) {
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            setMentionQuery(null);
            return;
        }

        const activeOption =
            filteredAssigneeOptions.find(
                (option) => option.id === activeMentionUserId,
            ) ?? filteredAssigneeOptions[0];
        if (
            activeOption &&
            !event.shiftKey &&
            (event.key === 'Enter' || event.key === 'Tab')
        ) {
            event.preventDefault();
            selectMention(activeOption.id, activeOption.name);
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (filteredAssigneeOptions.length === 0) {
                return;
            }
            event.preventDefault();
            const currentIndex = filteredAssigneeOptions.findIndex(
                (option) => option.id === activeMentionUserId,
            );
            const nextIndex =
                event.key === 'ArrowDown'
                    ? (currentIndex + 1) % filteredAssigneeOptions.length
                    : (currentIndex - 1 + filteredAssigneeOptions.length) %
                      filteredAssigneeOptions.length;
            setActiveMentionUserId(
                filteredAssigneeOptions[nextIndex]?.id ?? null,
            );
        }
    }

    async function submitNote() {
        const body = draft.trim();
        if (!body || submitting) {
            return;
        }
        setSubmitting(true);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/conversations/{conversation_id}/messages/{message_id}/notes',
                pathParams: {
                    organization_id: organizationId,
                    conversation_id: conversationId,
                    message_id: messageId,
                },
                body: {
                    body,
                    mentioned_user_id: mentionedUserId,
                },
                reason: 'conversation-message-note-create',
            });
            setNotes((currentNotes) =>
                currentNotes.some((note) => note.id === response.note.id)
                    ? currentNotes
                    : [...currentNotes, response.note],
            );
            setDraft('');
            setMentionedUserId(null);
            setMentionedUserName(null);
            setMentionQuery(null);
            onNoteCreated?.();
            try {
                await onConversationMutated?.();
            } catch {
                toast.error(
                    'Note added, but conversation details couldn’t refresh',
                    {
                        persistNotification: false,
                    },
                );
            }
        } catch {
            toast.error('Couldn’t add internal note', {
                persistNotification: false,
            });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="overflow-hidden rounded-lg border bg-amber-50 text-amber-950 dark:bg-amber-950/30 dark:text-amber-50"
            data-testid="internal-note-thread"
        >
            <div className="flex h-9 items-center justify-between px-2.5">
                <Tooltip>
                    <TooltipTrigger asChild={true}>
                        <span
                            aria-label="Internal thread. Only your team can see these notes."
                            className="flex cursor-help items-center gap-1.5"
                            tabIndex={0}
                        >
                            <MessageSquareText className="size-3.5 text-amber-600 dark:text-amber-400" />
                            <span className="text-xs font-medium">
                                Internal thread
                            </span>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={6}>
                        Only your team can see these notes.
                    </TooltipContent>
                </Tooltip>
                <Button
                    aria-label="Collapse internal thread"
                    className="text-muted-foreground size-7"
                    onClick={onCollapse}
                    size="icon"
                    type="button"
                    variant="ghost"
                >
                    <ChevronUp className="size-3.5" />
                </Button>
            </div>
            {loadError ? (
                <div
                    className="text-muted-foreground flex items-center justify-between gap-2 border-t px-3 py-2 text-xs"
                    role="alert"
                >
                    <p>
                        {loadState.hasLoaded
                            ? 'Couldn’t refresh internal notes.'
                            : 'Couldn’t load internal notes.'}
                    </p>
                    <Button
                        aria-label="Retry loading internal notes"
                        disabled={loading}
                        onClick={() => void loadLatest()}
                        size="sm"
                        type="button"
                        variant="ghost"
                    >
                        {loading ? 'Retrying…' : 'Retry'}
                    </Button>
                </div>
            ) : null}
            {loading || notes.length > 0 || hasMore ? (
                <div className="px-3 py-2.5">
                    {loading && notes.length === 0 ? (
                        <div className="flex justify-center py-2">
                            <Spinner className="size-4" />
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {hasMore ? (
                                <Button
                                    className="h-7 w-full text-xs"
                                    disabled={loadingOlder}
                                    onClick={() => void loadOlderNotes()}
                                    size="sm"
                                    type="button"
                                    variant="ghost"
                                >
                                    {loadingOlder ? (
                                        <Spinner className="size-3.5" />
                                    ) : (
                                        'Show earlier notes'
                                    )}
                                </Button>
                            ) : null}
                            {notes.map((note) => {
                                const isOwnNote =
                                    note.created_by_user_id === currentUser?.id;
                                return (
                                    <div
                                        className={cn(
                                            'flex flex-col gap-1',
                                            isOwnNote
                                                ? 'items-end'
                                                : 'items-start',
                                        )}
                                        data-testid="internal-note-row"
                                        key={note.id}
                                    >
                                        <div className="flex max-w-[85%] items-baseline gap-2 px-1 text-[11px]">
                                            <span className="font-medium">
                                                {note.created_by_user_name}
                                            </span>
                                            <span className="text-muted-foreground shrink-0">
                                                {noteTimestamp(
                                                    note.created_at,
                                                    timezone,
                                                )}
                                            </span>
                                        </div>
                                        <div
                                            className={cn(
                                                'max-w-[85%] rounded-2xl px-3 py-2',
                                                isOwnNote
                                                    ? 'rounded-tr-sm bg-amber-200 text-amber-950 dark:bg-amber-800/70 dark:text-amber-50'
                                                    : 'rounded-tl-sm bg-amber-100 text-amber-950 dark:bg-amber-900/60 dark:text-amber-100',
                                            )}
                                            data-testid="internal-note-bubble"
                                        >
                                            <p className="text-sm [overflow-wrap:anywhere] whitespace-pre-wrap">
                                                {note.body}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : null}
            <div className="relative border-t p-2">
                <Textarea
                    aria-label="Internal note"
                    className="max-h-28 min-h-10 resize-none border-0 bg-transparent px-1 py-2 pr-9 text-sm shadow-none focus-visible:border-transparent focus-visible:ring-0"
                    disabled={submitting}
                    maxLength={NOTE_MAX_LENGTH}
                    onChange={handleDraftChange}
                    onKeyDown={handleDraftKeyDown}
                    placeholder="Add a note… Use @ to mention"
                    ref={textareaRef}
                    value={draft}
                />
                <Button
                    aria-label="Send internal note"
                    className="text-muted-foreground hover:text-foreground absolute right-2 bottom-2 size-7 hover:bg-transparent"
                    disabled={draft.trim().length === 0 || submitting}
                    onClick={() => void submitNote()}
                    size="icon"
                    type="button"
                    variant="ghost"
                >
                    {submitting ? (
                        <Spinner className="h-3.5 w-3.5" />
                    ) : (
                        <ArrowUp className="size-4" />
                    )}
                </Button>
                {mentionQuery !== null ? (
                    <div className="bg-popover text-popover-foreground absolute right-0 bottom-full left-0 z-20 mb-1 overflow-hidden rounded-md border shadow-md">
                        <Command
                            onValueChange={setActiveMentionUserId}
                            shouldFilter={false}
                            value={activeMentionUserId ?? ''}
                        >
                            <div className="text-muted-foreground border-b px-3 py-2 text-xs">
                                Assign to…
                            </div>
                            <CommandList className="max-h-44">
                                <CommandEmpty>No people found.</CommandEmpty>
                                <CommandGroup>
                                    {filteredAssigneeOptions.map((option) => (
                                        <CommandItem
                                            key={option.id}
                                            onSelect={() =>
                                                selectMention(
                                                    option.id,
                                                    option.name,
                                                )
                                            }
                                            value={option.id}
                                        >
                                            {option.label}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
